import React from 'react';
import PropTypes from 'prop-types';
import { Button, Table } from 'bootstrap-4-react/lib/components';
import { MenuList } from '../../common/MenuList';
import { Dropdown } from 'react-bootstrap';
import BatchPagination from '../../batch/BatchPagination';
import { numOfPage } from '../../../constant';
import { renderTableRow } from '../../../config/utils';
import moment from 'moment';

// Column configuration for table headers
export const COLUMNS_CONFIG = [
    { key: 'CompanySellerName', label: 'Company Seller Name', sortable: false },
    { key: 'Email', label: 'Email', sortable: false },
    { key: 'CreatedDate', label: 'Created At', sortable: false },
    { key: 'InvitationUrl', label: 'URL', sortable: false },
];

// Subcomponent: Page Header
export const AffiliatePageHeader = ({ onAddClick, onBackClick }) => (
    <div className="main__tile_heading cus-btn-include-sec">
        Partner Management
        <div className="top_right_button_container">
            <Button className="cus-primary-bg-btn" onClick={onAddClick}>
                Add Company/Seller
            </Button>
            <Button className="cus-primary-transparent-btn" onClick={onBackClick}>
                Back
            </Button>
        </div>
    </div>
);

AffiliatePageHeader.propTypes = {
    onAddClick: PropTypes.func.isRequired,
    onBackClick: PropTypes.func.isRequired,
};

// Subcomponent: Table Header
export const AffiliateTableHeader = ({ sortColumn, sortOrder, onSort }) => (
    <thead border="Green">
        <tr className="trow">
            <th className="trows-s">#</th>
            {COLUMNS_CONFIG.map(column => (
                <th key={column.key} className={column.key === 'Status' ? 'trows-s' : ''}>
                    {column.sortable ? (
                        <button
                            type="button"
                            className={sortColumn === column.key ? 'active sortBtn' : 'sortColumn sortBtn'}
                            onClick={e => onSort(e, column.key)}
                        >
                            {column.label}{' '}
                            <i
                                className={
                                    sortColumn === column.key && sortOrder === 'ASCENDING'
                                        ? 'fa fa-arrow-up'
                                        : 'fa fa-arrow-down'
                                }
                                aria-hidden="true"
                            ></i>
                        </button>
                    ) : (
                        column.label
                    )}
                </th>
            ))}
            <th className="trows-s"></th>
        </tr>
    </thead>
);

AffiliateTableHeader.propTypes = {
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
};

// Subcomponent: Table Row
export const AffiliateTableRow = ({ item, index, startIdx, onDelete, onCopy }) => (
    <tr key={item.Id}>
        <td className="trows-s">{startIdx + index + 1}</td>
        <td className="trows-s">{item.CompanySellerName || 'N/A'}</td>
        <td className="trows-s">{item.Email || 'N/A'}</td>
        <td className="trows-s">{item.CreatedDate ? moment(item.CreatedDate).format('MM-DD-YYYY HH:mm') : 'N/A'}</td>
        <td className="trows-s">
            {item.InvitationUrl ? (
                <div className="keyFieldWrapper">
                    <input
                        value={item.InvitationUrl}
                        className="form-control"
                    />
                    <button
                        type="button"
                        onClick={(e) => onCopy(e, item)}
                        className="btn copyBtn"
                        title="Copy URL"
                    >
                        <i className="fa fa-clone" aria-hidden="true"></i>
                    </button>
                </div>
            ) : (
                'N/A'
            )}
        </td>
        <td>
            <MenuList>
                <Dropdown.Item className="printLabelBtn" onClick={() => onDelete(item.Id)}>
                    Delete
                </Dropdown.Item>
            </MenuList>
        </td>
    </tr>
);

AffiliateTableRow.propTypes = {
    item: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        CompanySellerName: PropTypes.string,
        Email: PropTypes.string,
        CreatedDate: PropTypes.string,
        InvitationUrl: PropTypes.string,
        InviteToken: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

// Subcomponent: Table Body
export const AffiliateTableBody = ({ data, startIdx, onDelete, onCopy }) =>
    data && data.length > 0 ? (
        <tbody>
            {data.map((item, index) => (
                <AffiliateTableRow key={item.Id} item={item} index={index} startIdx={startIdx} onDelete={onDelete} onCopy={onCopy} />
            ))}
        </tbody>
    ) : (
        <tbody>{renderTableRow(8, 'No Record Found')}</tbody>
    );

AffiliateTableBody.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

// Subcomponent: Table (combines header and body)
export const AffiliateTable = ({ data, sortColumn, sortOrder, onSort, startIdx, onDelete, onCopy }) => (
    <Table className="table affiliateTable keyGenerate">
        <AffiliateTableHeader sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} />
        <AffiliateTableBody data={data} startIdx={startIdx} onDelete={onDelete} onCopy={onCopy} />
    </Table>
);

AffiliateTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

// Subcomponent: Pagination Section
export const AffiliatePaginationSection = ({ paginatedData, pagination, affiliateDataLength }) => {
    if (!paginatedData?.length) return null;

    return (
        <BatchPagination
            gridData={paginatedData}
            numberOfPage={pagination.numberOfPage}
            numOfPage={numOfPage}
            loader={false}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            setCurrentPage={pagination.setCurrentPage}
            totalRecords={affiliateDataLength}
            setNumberOfPage={pagination.setNumberOfPage}
        />
    );
};

AffiliatePaginationSection.propTypes = {
    paginatedData: PropTypes.arrayOf(PropTypes.object).isRequired,
    pagination: PropTypes.shape({
        numberOfPage: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        setCurrentPage: PropTypes.func.isRequired,
        setNumberOfPage: PropTypes.func.isRequired,
    }).isRequired,
    affiliateDataLength: PropTypes.number.isRequired,
};
